import React from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, Container, DisplayText, theme, useResponsive } from '@sg/garnish';
import type { DietaryPreferencesTypes } from '@sg/shared-fe-features-menu';

import { useHomeContent } from '../../hooks';
import { HomeCategory } from '../HomeCategory';
import { HomeProductCard } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategories = (props: HomeGenericCategoriesProps) => {
  const {
    genericCategories,
    dietaryPreferences,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { currentBreakpoint, match } = useResponsive();

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Container style={match([styles.containerXS, styles.containerSM])}>
      {genericCategories.map((category) => (
        <View key={category.id} style={styles.category}>
          {currentBreakpoint.isXS ? (
            <BodyText sizeMatch={['18']}>{category.categoryName}</BodyText>
          ) : (
            <DisplayText style={styles.titleSM} sizeMatch={['32']}>
              {category.categoryName}
            </DisplayText>
          )}

          <HomeCategory>
            {category.products.slice(0, 3).map((product) => (
              <HomeProductCard
                key={product.baseSlug}
                categoryName={category.categoryName}
                id={product.id}
                slug={product.slug}
                title={product.title}
                cost={product.cost}
                description={product.description}
                contentfulImage={product.image}
                isModifiable={product.isModifiable}
                dietaryPreferences={dietaryPreferences}
                allergenProperties={product.allergenProperties}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={addProductToBag}
                trackItemSelection={trackItemSelection}
              />
            ))}
          </HomeCategory>
        </View>
      ))}
    </Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  containerSM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
  },
  titleSM: {
    textAlign: 'center',
  },
  category: {
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeGenericCategoriesProps = {
  genericCategories: ReturnType<typeof useHomeContent>['genericCategories'];
  dietaryPreferences: DietaryPreferencesTypes;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (product: string, category: string) => void;
};
